import { useEffect, useRef } from "react";
import { useConnectGoogle, useGoogleStatus } from "@/lib/google";

/**
 * Offers the Google Calendar consent immediately after a Google sign-in.
 *
 * Signing in with Google cannot grant calendar access on its own — Neon Auth
 * owns that OAuth client and will not widen its scopes — so the closest thing to
 * "it just syncs" is to run the second consent the moment the user lands.
 *
 * Two guards keep that from being a trap:
 *   - it only fires when this session actually came from the Google button
 *     (`SIGNED_IN_WITH_GOOGLE`, set on click and cleared on read), and
 *   - it fires at most once per user, ever (`ASKED`). Declining is respected;
 *     Settings is then the only way back in.
 */

export const SIGNED_IN_WITH_GOOGLE = "sebas.signed-in-with-google";
const ASKED = "sebas.google-calendar-asked";

export function GoogleCalendarAutoConnect() {
  const { data: status } = useGoogleStatus();
  const connect = useConnectGoogle();
  // React runs effects twice in StrictMode; without this the consent would be
  // kicked off two times and the second would race the first's navigation.
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current || !status?.configured || status.connected) return;

    let cameFromGoogle = false;
    try {
      cameFromGoogle = sessionStorage.getItem(SIGNED_IN_WITH_GOOGLE) === "1";
      if (!cameFromGoogle) return;
      sessionStorage.removeItem(SIGNED_IN_WITH_GOOGLE);
      if (localStorage.getItem(ASKED) === "1") return;
      localStorage.setItem(ASKED, "1");
    } catch {
      // Private mode or a locked-down webview: skip rather than ask every load.
      return;
    }

    fired.current = true;
    connect.mutate();
  }, [status, connect]);

  if (!connect.isPending) return null;

  return (
    <div className="fixed inset-x-0 top-0 z-50 bg-accent px-5 py-2 text-center text-xs font-medium text-accent-ink">
      Connecting your Google Calendar…
    </div>
  );
}
